import React, { useState } from 'react';
import { useAdminUsers } from '../../hooks/useAdminUsers';
import { useAdminRoles } from '../../hooks/useAdminRoles';
import { UserForm } from '../../components/Admin/UserForm';
import { UserTable } from '../../components/Admin/UserTable';
import { EditUserModal } from '../../components/Admin/EditUserModal';
import { AdminUser, CreateUserRequest } from '../../types/admin';

export const UsersManagement: React.FC = () => {
  const { users, loading, error, createUser, deleteUser, updateUser } = useAdminUsers();
  const { roles } = useAdminRoles();
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  const handleCreateUser = async (data: CreateUserRequest) => {
    try {
      setFormLoading(true);
      await createUser(data);
      setShowForm(false);
      // Show success message
      alert('User created successfully!');
    } catch (err) {
      console.error('Failed to create user:', err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditUser = (user: AdminUser) => {
    setEditingUser(user);
  };

  const handleSaveUser = async (data: { firstName?: string; lastName?: string; roleId: string; status: string }) => {
    if (!editingUser) return;

    try {
      setEditLoading(true);
      await updateUser(editingUser.id, data);
      setEditingUser(null);
      alert('User updated successfully!');
    } catch (err) {
      console.error('Failed to update user:', err);
      alert('Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async (user: AdminUser) => {
    if (window.confirm(`Are you sure you want to delete ${user.email}?`)) {
      try {
        await deleteUser(user.id);
        alert('User deleted successfully!');
      } catch (err) {
        console.error('Failed to delete user:', err);
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Users Management</h1>
        <button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          {showForm ? 'Cancel' : '+ Create User'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {showForm && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">Create New User</h2>
          <UserForm
            roles={roles}
            onSubmit={handleCreateUser}
            onCancel={() => setShowForm(false)}
            loading={formLoading}
          />
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Users List ({users.length})</h2>
        <UserTable
          users={users}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
          loading={loading}
        />
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          roles={roles}
          onSave={handleSaveUser}
          onCancel={() => setEditingUser(null)}
          loading={editLoading}
        />
      )}
    </div>
  );
};

